/**
 * The accessibility floor — enforced here, never in the prompt.
 *
 * The generator is untrusted output. Anything that must always hold belongs in
 * the runtime, which is the last line of defence and the only layer we control
 * end to end.
 *
 * And the runtime **refuses rather than degrades**. A layout that would render
 * illegibly at the current viewport is a failed layout, not a layout to ship
 * slightly worse. Shipping it teaches the taste model that it was acceptable.
 */

import { ROLE_KIND, AFFORDANCES } from '@morph/spec';
import type { IR, IRNode, ViewportClass } from './ir.js';

export interface Violation {
  node: number;
  code: string;
  message: string;
}

/** Minimum legible body size in CSS px. Below this, text is decoration. */
const MIN_BODY_PX = 12;
/** Below this a card is a swatch, not a card — it can no longer carry a label. */
const MIN_CARD_PX = 96;
/** WCAG 2.5.8 target minimum. Exported because `tokens.ts` resolves the inline
 *  edit target against it: a floor the layer producing the number cannot see is
 *  a floor that gets missed, which is how a 21px edit target shipped. */
export const MIN_TARGET_PX = 24;

/**
 * Every affordance is interactive. Derived from the vocabulary rather than
 * listed, because the listed copy went stale the moment `invoke` was added:
 * a button would not have counted as interactive, so nothing would have
 * required it to be nameable — the same shape as the bug the comment below
 * describes, one feature later. A hand-maintained mirror of a closed set is a
 * bug with a delay on it.
 */
const INTERACTIVE = new Set<string>(AFFORDANCES);

/**
 * There is deliberately NO rule here refusing a docked panel that crowds out the
 * content, and the first version of this file had one.
 *
 * An adversarial pass measured a real failure — at 390px a panel docked to an
 * inline edge left the content column at width zero, off-screen, unreported —
 * and a floor rule looked like the answer. It was not. This module refuses
 * rather than degrades, so the rule made the demo refuse to render at all on a
 * phone: correct by the letter of the contract and useless as a product.
 *
 * The real cause was the stylesheet claiming "beside" at a width where beside
 * does not exist. `render-html.ts` now stacks an inline-docked panel below
 * 34rem, so the layout is legible and there is nothing to refuse. A floor rule
 * kept alongside that fix would only ever reject layouts that render correctly,
 * and a safety check that never fires on a real failure is worse than none — it
 * is a thing people learn to route around.
 */

export function checkFloor(ir: IR, viewport: ViewportClass): Violation[] {
  const v: Violation[] = [];
  const push = (node: number, code: string, message: string) => v.push({ node, code, message });

  if (ir.tokens.scale[0]! < MIN_BODY_PX) {
    push(ir.root, 'body-too-small',
      `body text resolves to ${ir.tokens.scale[0]}px, below the ${MIN_BODY_PX}px floor`);
  }
  if (ir.tokens.minTargetPx < MIN_TARGET_PX) {
    push(ir.root, 'target-too-small',
      `interactive targets resolve to ${ir.tokens.minTargetPx}px`);
  }

  for (const n of ir.nodes) {
    const interactive = (n.affordances ?? []).some((a) => INTERACTIVE.has(a));

    // An inline edit target is a target, and it was the one class of node this
    // function could not see: it tested `ir.tokens.minTargetPx`, a constant that
    // always passes, and `n.affordances`, which compile.ts never put on a field
    // node. Meanwhile nothing in the renderer consumed the 44px the token
    // promised, so an editable cell resolved to the height of its line box —
    // about 21px at compact width, under this floor and unreported.
    if (n.direction === 'read-write' && ir.tokens.editTargetPx < MIN_TARGET_PX) {
      push(n.i, 'edit-target-too-small',
        `an inline edit target resolves to ${ir.tokens.editTargetPx}px, `
        + `below the ${MIN_TARGET_PX}px floor`);
    }

    // Every interactive node must be nameable. A button a screen reader
    // announces as "button" is not usable, and the generator cannot be trusted
    // to have thought about it.
    if (interactive && !hasAccessibleName(ir, n)) {
      push(n.i, 'no-accessible-name',
        `${n.kind} is interactive but nothing in its subtree can name it`);
    }

    // A dense grid on a compact viewport is the case Codex's density-boundary
    // study showed failing: targets shrink, thumbnails stop carrying identity.
    if (n.kind === 'collection' && n.flow === 'grid' && viewport === 'compact'
        && ir.tokens.density === 'dense') {
      push(n.i, 'grid-too-dense-for-viewport',
        'a dense grid is not operable at compact width');
    }

    if (n.kind === 'media' && viewport === 'compact' && estimateCardPx(ir, n) < MIN_CARD_PX) {
      push(n.i, 'media-below-identity-threshold',
        'media renders too small to carry identity, so it is chrome rather than content');
    }

  }
  return v;
}

/** Only a role that resolves to *words* can name something. A media binding is
 *  emitted as a field node like any other, so checking the node kind alone
 *  reported a poster-only card as nameable — which is exactly the card a screen
 *  reader announces as "button". */
const NAMING_KINDS = new Set(['text', 'enum', 'date', 'number']);

function hasAccessibleName(ir: IR, node: IRNode): boolean {
  if (node.nameFrom) return true;
  // An editable field names ITSELF: the renderer derives an `aria-label` from
  // the role it binds (see `editAttrs` in render-html.ts), so the name exists for
  // every one of them. Walking the subtree instead reported each editable cell —
  // a leaf, with no subtree — as unnamed, and a floor that refuses the layouts
  // it exists to protect gets deleted rather than fixed.
  if (node.direction === 'read-write' && node.role) return true;
  // A button names ITSELF for the same reason: `invokeButton` in render-html.ts
  // derives both its visible label and its `aria-label` from the effects the
  // invocation declares, so the name exists for every one of them and has no
  // subtree to be found in. Without this, adding `invoke` to `INTERACTIVE` —
  // which is the right fix — would refuse every button in the product.
  if (node.invocation) return true;
  const stack = [...node.children];
  while (stack.length) {
    const child = ir.nodes[stack.pop()!];
    if (!child) continue;
    if (child.nameFrom) return true;
    if (child.role && child.kind === 'field' && NAMING_KINDS.has(ROLE_KIND[child.role])) {
      return true;
    }
    stack.push(...child.children);
  }
  return false;
}

/** Crude, and deliberately so — a real measurement needs a layout pass, and
 *  this only has to catch the obvious failures the generator produces. */
function estimateCardPx(ir: IR, node: IRNode): number {
  const columns = ir.tokens.density === 'dense' ? 6 : ir.tokens.density === 'balanced' ? 4 : 3;
  const width = 390;   // compact reference width
  return Math.floor((width - ir.tokens.gapPx * (columns + 1)) / columns);
}
